import { call, put, takeEvery, fork, select } from 'redux-saga/effects';
import { normalize } from 'normalizr';
import { startSubmit, stopSubmit, reset as resetForm } from 'redux-form';
import { hide as hideModal } from 'redux-modal';

import * as schemas from 'store/common/schemas';
import * as entityActions from 'store/entities/actions';
import * as actionTypes from './action-types';
import * as actions from './actions';
import * as api from 'api/config/companies';
import { selectCompanyByPrettyId } from 'store/features/companies/selectors';
import { selectCompanyTeamMemberConnectionById } from 'store/features/company-team-member-connections/selectors';
import callApi from 'store/api/saga';
import { addMessage } from 'store/features/common/flash/actions';
import { messageTypes } from 'store/features/common/flash/builder';
import { confirmSaga as confirm } from 'store/features/common/confirmation-dialog/saga';

import * as teamMembersMessages from 'ui/companies/edit/team-members/messages';

function* fetchCompanyTeamMembersConnectionsForAdminByPrettyId({ payload }) {
  const { prettyId } = payload;
  yield put(actions.fetchCompanyTeamMemberConnectionsForAdminByPrettyId.start({ prettyId }));

  try {
    const response = yield call(
      callApi,
      api.fetchCompanyTeamMemberConnectionsAsAdminByPrettyId({ prettyId })
    );
    const schema = schemas.company;
    const { entities } = normalize({ prettyId, teamMembers: response }, schema);
    yield put(entityActions.mergeEntities(entities));
    yield put(actions.fetchCompanyTeamMemberConnectionsForAdminByPrettyId.success({ prettyId }));
  } catch (error) {
    yield put(
      actions.fetchCompanyTeamMemberConnectionsForAdminByPrettyId.failure({ prettyId, error })
    );
    yield put(addMessage({ kind: messageTypes.danger, content: error.message }));
  }
}

function* watchFetchCompanyTeamMembersConnectionsForAdminByPrettyId() {
  yield takeEvery(
    actionTypes.FETCH_COMPANY_TEAM_MEMBER_CONNECTIONS_FOR_ADMIN_BY_PRETTY_ID.REQUEST,
    fetchCompanyTeamMembersConnectionsForAdminByPrettyId
  );
}

function* inviteTeamMemberToCompany({ payload: values, meta = {} }) {
  const { name, imgUrl, email, designation, prettyId } = values;
  const { form, modal } = meta;
  yield put(actions.inviteTeamMemberToCompany.start(values));
  if (form) yield put(startSubmit(form));

  try {
    let response = yield call(
      callApi,
      api.inviteTeamMemberToCompany({ name, imgUrl, email, designation, prettyId })
    );

    response = { ...response, name, imgUrl, email, designation };
    if (form) yield put(stopSubmit(form));
    if (form) yield put(resetForm(form));
    const schema = schemas.companyTeamMemberConnection;
    const { entities } = normalize(response, schema);
    yield put(entityActions.mergeEntities(entities));
    yield put(
      actions.inviteTeamMemberToCompany.success({ teamMemberConnection: response, prettyId })
    );
    if (modal) {
      yield put(hideModal(modal));
    }
  } catch (error) {
    if (form) yield put(stopSubmit(form, error.formErrors));
    yield put(actions.inviteTeamMemberToCompany.failure({ email, designation, prettyId, error }));
    yield put(
      addMessage({ kind: messageTypes.danger, content: error.message || error.formErrors })
    );
  }
}

function* watchInviteTeamMemberToCompany() {
  yield takeEvery(actionTypes.INVITE_TEAM_MEMBER_TO_COMPANY.REQUEST, inviteTeamMemberToCompany);
}

function* updateTeamMemberToCompany({ payload: values, meta = {} }) {
  const { name, imgUrl, designation, prettyId, teamMemberId, isRegisteredUser } = values;
  const { form, modal } = meta;
  yield put(actions.updateTeamMemberToCompany.start(values));
  if (form) yield put(startSubmit(form));

  try {
    let response = yield call(callApi, api.updateTeamMemberToCompany(values));

    response = { ...response, designation, id: teamMemberId };
    response = isRegisteredUser ? response : { ...response, name, imgUrl };

    if (form) yield put(stopSubmit(form));
    if (form) yield put(resetForm(form));
    const schema = schemas.companyTeamMemberConnection;
    const { entities } = normalize(response, schema);
    yield put(entityActions.mergeEntities(entities));
    yield put(
      actions.updateTeamMemberToCompany.success({ teamMemberConnection: response, prettyId })
    );
    if (modal) {
      yield put(hideModal(modal));
    }
  } catch (error) {
    if (form) yield put(stopSubmit(form, error.formErrors));
    yield put(actions.updateTeamMemberToCompany.failure({ prettyId, error }));
    yield put(
      addMessage({ kind: messageTypes.danger, content: error.message || error.formErrors })
    );
  }
}

function* watchUpdateTeamMemberToCompany() {
  yield takeEvery(actionTypes.UPDATE_TEAM_MEMBER_TO_COMPANY.REQUEST, updateTeamMemberToCompany);
}

function* deleteTeamMemberFromCompany({ payload }) {
  const { teamMemberId, prettyId } = payload;

  const company = yield select(selectCompanyByPrettyId, { prettyId });
  const teamMember = yield select(selectCompanyTeamMemberConnectionById, { id: teamMemberId });

  const confirmationMessage = {
    ...teamMembersMessages.connectionActions.confirmDelete,
  };
  const confirmed = yield call(confirm, { message: confirmationMessage });
  if (!confirmed) {
    return;
  }

  yield put(actions.deleteTeamMemberFromCompany.start({ teamMemberId, prettyId }));

  try {
    yield call(callApi, api.deleteTeamMemberFromCompany({ teamMemberId, prettyId }));
    const schema = schemas.companyTeamMemberConnection;
    yield put(entityActions.removeEntities({ [schema.key]: [teamMemberId] }));

    const successMessage = {
      ...teamMembersMessages.connectionActions.deleted,
      values: {
        companyName: company.companyName,
        teamMemberName:
          (teamMember && teamMember.user && teamMember.user.name) ||
          (teamMember && teamMember.email) ||
          '',
      },
    };

    yield put(actions.deleteTeamMemberFromCompany.success({ teamMemberId, prettyId }));
    yield put(addMessage({ kind: messageTypes.success, content: successMessage }));
  } catch (error) {
    yield put(actions.deleteTeamMemberFromCompany.failure({ teamMemberId, prettyId, error }));
    yield put(addMessage({ kind: messageTypes.danger, content: error.message }));
  }
}

function* watchDeleteTeamMemberFromCompany() {
  yield takeEvery(actionTypes.DELETE_TEAM_MEMBER_FROM_COMPANY.REQUEST, deleteTeamMemberFromCompany);
}

export default function* companies() {
  yield fork(watchFetchCompanyTeamMembersConnectionsForAdminByPrettyId);
  yield fork(watchInviteTeamMemberToCompany);
  yield fork(watchDeleteTeamMemberFromCompany);
  yield fork(watchUpdateTeamMemberToCompany);
}



// WEBPACK FOOTER //
// ./src/store/features/admin/companies/team-members/saga.js